import { type ChangeEvent, type HTMLAttributes, type ReactNode, useId } from "react";
import { Field } from "react-final-form";
import { tw } from "../../styling";
import type { BaseFieldProps, SimpleFieldValue } from "../../types/form";
import { composeValidators } from "../../utils/validation";
import { TextAreaInput as BaseTextArea } from "./components/text-area";
import { FormHelperText } from "./form-helper-text";

type Props<T extends SimpleFieldValue = string> = BaseFieldProps<T> & {
	inputMode?: HTMLAttributes<string>["inputMode"];
	label: string;
	id?: string;
	helperText?: string;
	handleChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
	disabled?: boolean;
	fullWidth?: boolean;
	endAdornment?: ReactNode;
	rows?: number;
};

export const TextAreaField = <T extends SimpleFieldValue = string>({
	id,
	label,
	inputMode,
	name,
	validators,
	helperText,
	placeholder,
	handleChange,
	onBlur,
	endAdornment,
	maxLength,
	autoComplete,
	rows,
	disabled = false,
	fullWidth = true,
	autoFocus = false,
	...otherProps
}: Props<T>) => {
	const describedBy = useId();
	return (
		<Field<T> name={name} validate={validators && composeValidators(...validators)} {...otherProps}>
			{({ input, meta }) => {
				const hasError = !!meta.error && !!meta.touched;

				const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement, Element>) => {
					onBlur?.(e);
					input.onBlur();
				};

				const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
					handleChange?.(e);
					input.onChange(e);
				};

				return (
					<div className={tw("flex flex-col gap-2", fullWidth && "w-full")}>
						<BaseTextArea
							{...input}
							value={input.value?.toString() || ""}
							id={id}
							fullWidth={fullWidth}
							type="text"
							inputMode={inputMode}
							placeholder={placeholder}
							label={label}
							error={hasError}
							disabled={disabled}
							onChange={onChange}
							endAdornment={endAdornment}
							maxLength={maxLength}
							autoComplete={autoComplete}
							onBlur={handleBlur}
							autoFocus={autoFocus}
							aria-describedby={describedBy}
							aria-invalid={hasError}
							rows={rows}
						/>
						<FormHelperText error={hasError} id={describedBy}>
							{hasError ? meta.error : helperText}
						</FormHelperText>
					</div>
				);
			}}
		</Field>
	);
};
